import React from 'react'
import { FormattedMessage, MessageDescriptor } from 'react-intl'
import { connect, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'

import { AxiosError } from 'axios'
import { isString } from 'lodash-es'

import { LW_API_ERROR_CODE } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/errorCodes'
import {
  expiredAccountErrorMessage,
  invalidRegistrationOktaPassword,
  loginErrorMessages,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/messages'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { toJS } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/hocs/toJS'

import { getPasswordResetFailedText } from '../../../data/customer/customerSelectors'
import { getCareTeamEmail } from '../../../data/user/userSelectors'
import { isInRegisterFlow } from '../../../features/register/data/registerUtils'
import { INVALID_PAYMENT_ERROR } from '../../constants/errorConstants'

export interface APIErrorMessageProps {
  statusCode?: number // http status code
  errorCode?: LW_API_ERROR_CODE // error code within response
  errorMessage?: string // error message within response
  requestUrl?: string
  requestMethod?: string
  passwordResetFailedText: MessageDescriptor
}

const APIErrorMessageImpl: React.FunctionComponent<APIErrorMessageProps> = ({
  statusCode,
  errorCode,
  errorMessage,
  passwordResetFailedText,
}) => {
  const { isMemberSwitchEmployerSelfServeEnabled } = useFlags()
  console.error('API error', { statusCode, errorCode, errorMessage })

  const careTeamHelpEmail = useSelector(getCareTeamEmail)
  const careTeamEmailLink = <a href={`mailto:${careTeamHelpEmail}`}>{careTeamHelpEmail}</a>

  switch (errorCode) {
    case 'USER_REGISTERED':
      return (
        <FormattedMessage
          defaultMessage='This account has already registered. Please use different email address or <link>click here to login</link>.'
          description='Error message when user is finishing registration of an account which has already been registered'
          values={{
            link: (text: string) => <Link to='/login'>{text}</Link>,
          }}
        />
      )

    case 'USER_REGISTERED_DIFFERENT_CUSTOMER':
      if (isMemberSwitchEmployerSelfServeEnabled) {
        return (
          <FormattedMessage
            defaultMessage='Your Lyra account is linked to another organization. To transfer it to a different company, log in and review your coverage information page in the account section.'
            description='Error message when user is logging in with an account which is already registered with a different employer and self-serve transfer is enabled'
          />
        )
      } else {
        return (
          <FormattedMessage
            defaultMessage='Your Lyra account is attached to a different organization. If you have switched organizations, please contact {careTeamEmailLink} to update your account. You may still log in below.'
            description='Error message when user is logging in with an account which is already registered with a different employer'
            values={{ careTeamEmailLink }}
          />
        )
      }

    case 'USER_ACCOUNT_UNRECOGNIZED':
      return (
        <FormattedMessage
          defaultMessage='We do not recognize that account. Please log in using another email address or create a new account.'
          description='Error message when user is logging in with an account which cannot be recognized'
        />
      )

    case 'USER_ACCOUNT_PENDING_VERIFICATION':
      const userEmail = errorMessage
      return (
        <FormattedMessage
          defaultMessage='Please check your email to finish setting up your account. Email address: {userEmail}.'
          description='Error message when user is logging in an account which has not completed registration'
          values={{ userEmail }}
        />
      )

    case 'INELIGIBLE_USER':
      return (
        <FormattedMessage
          defaultMessage='Something went wrong. Please check your provided information and ensure that it is correct. If you keep getting this error, please contact {careTeamEmailLink}.'
          description='Error message when user is identified as ineligible'
          values={{ careTeamEmailLink }}
        />
      )

    case 'INVALID_FACTOR_CODE':
      return (
        <FormattedMessage
          defaultMessage='Invalid passcode'
          description='Error message when user entered incorrect passcode for enabling multi-factor authentication'
        />
      )

    case 'INVALID_PAYMENT_INFO':
      return <FormattedMessage {...INVALID_PAYMENT_ERROR} />

    case 'INVALID_OKTA_PASSWORD':
      if (isInRegisterFlow(window.location.pathname)) {
        return <FormattedMessage {...invalidRegistrationOktaPassword} />
      }
      return <FormattedMessage {...passwordResetFailedText} />

    case 'USER_ACCOUNT_EXPIRED_MESSAGE':
      return <FormattedMessage {...expiredAccountErrorMessage} />

    case 'SSO_LOGIN_EMAIL_CONFLICT':
      return <FormattedMessage {...loginErrorMessages.SSO_LOGIN_EMAIL_CONFLICT} />

    default:
      if (statusCode === 401) {
        return (
          <FormattedMessage
            defaultMessage='Authorization failed for the given request. Please make sure you are logged in.'
            description='Error message when authorization error occurs'
          />
        )
      }

      if (errorMessage?.includes('An object with this field already exists in the current organization')) {
        return (
          <FormattedMessage
            defaultMessage='The email associated with this registration is already linked to an existing Lyra account. Please use a different email and try again.'
            description='Error message when user who came from a partner idp attempts to register an account with an email already associated with a Lyra Connect account'
          />
        )
      }

      return (
        <FormattedMessage
          defaultMessage='Something went wrong. If you keep getting this error, please contact {careTeamEmailLink}.'
          description='Error message when unexpected error occurs'
          values={{ careTeamEmailLink }}
        />
      )
  }
}

const mapStateToProps = (state: $TSFixMe) => {
  return {
    passwordResetFailedText: getPasswordResetFailedText(state),
  }
}

type APIErrorResponse = { message?: string; errorCode?: LW_API_ERROR_CODE } | string

export function buildAPIErrorMessageProps(error: Error): Partial<APIErrorMessageProps> {
  const axiosError = error as Partial<AxiosError<APIErrorResponse>>
  const errorResponse = axiosError.response?.data
  const responseDetails = isString(errorResponse) ? undefined : errorResponse
  const responseErrorCode = responseDetails?.errorCode
  const statusCode = axiosError.response?.status
  const errorCode = isString(responseErrorCode) ? responseErrorCode : undefined
  const errorMessage = isString(errorResponse)
    ? errorResponse
    : (responseDetails?.message ?? (axiosError.isAxiosError ? undefined : error.message))
  const requestUrl = axiosError.config?.url
  const requestMethod = axiosError.config?.method?.toUpperCase()

  return { statusCode, errorCode, errorMessage, requestUrl, requestMethod }
}

// @ts-expect-error TS(2345): Argument of type '(wrappedComponentProps: APIError... Remove this comment to see the full error message
export const APIErrorMessage = connect(mapStateToProps)(toJS(APIErrorMessageImpl))
